import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, Bell, Loader2, BookOpen, Video, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useBrandName } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { BACKEND_URL, BTN_PRIMARY, BTN_OUTLINE } from '../config/brand';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import FeatureCard from '../components/FeatureCard';
import IconBadge from '../components/IconBadge';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import EditableText from '../components/EditableText';

const inputClass =
  'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#3a00e0]';

const ACADEMY_FEATURES = [
  {
    icon: Video,
    variant: 'purple',
    title: '+50 modules vidéo',
    description: 'Des cours progressifs du débutant au confirmé, accessibles à tout moment',
  },
  {
    icon: BookOpen,
    variant: 'orange',
    title: 'Exercices pratiques',
    description: 'Des projets concrets pour mettre en application chaque leçon',
  },
  {
    icon: GraduationCap,
    variant: 'gradient',
    title: 'Inclus au cursus',
    description: 'Accès gratuit pour tous les inscrits au cursus annuel',
  },
];

const Elearning = () => {
  const brandName = useBrandName();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!BACKEND_URL) {
      toast({ title: 'Erreur', description: 'Service indisponible', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name || 'Prospect e-learning',
          email,
          message: `Inscription liste d'attente e-learning — ${brandName} Academy`,
          tag: 'elearning-waitlist',
        }),
      });
      if (!res.ok) throw new Error('Envoi impossible');
      toast({
        title: 'Inscription enregistrée !',
        description: 'Nous vous préviendrons dès le lancement de la plateforme.',
      });
      setEmail('');
      setName('');
    } catch {
      toast({
        title: 'Erreur',
        description: 'Impossible de vous inscrire. Réessayez plus tard.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        eyebrow="Bientôt disponible"
        eyebrowKey="elearning.hero.eyebrow"
        title={`${brandName} Academy arrive bientôt`}
        titleKey="elearning.hero.title"
        subtitle="Notre plateforme e-learning avec plus de 50 modules vidéo est en cours de préparation. Inscrivez-vous à un cursus ou réservez un stage pour en profiter en avant-première."
        subtitleKey="elearning.hero.subtitle"
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full max-w-md sm:max-w-none mx-auto">
          <Link to="/cursus" className="w-full sm:w-auto">
            <Button size="lg" className={`${BTN_PRIMARY} w-full`}>
              <EditableText editKey="elearning.hero.cta_primary" defaultValue="Voir les cursus" as="span" />
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Link to="/reserver" className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className={`${BTN_OUTLINE} w-full`}>
              <EditableText editKey="elearning.hero.cta_secondary" defaultValue="Réserver un atelier" as="span" />
            </Button>
          </Link>
        </div>
      </PageHero>

      <CircuitDivider className="mb-0" />

      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Plateforme"
            eyebrowKey="elearning.features.eyebrow"
            title="Ce que propose l'Academy"
            titleKey="elearning.features.title"
            subtitle="Une ressource complémentaire pour prolonger l'apprentissage à la maison."
            subtitleKey="elearning.features.subtitle"
            reveal
          />

          <div className="grid md:grid-cols-3 gap-6">
            {ACADEMY_FEATURES.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <FeatureCard
                  {...item}
                  titleKey={`elearning.features.${i + 1}.title`}
                  descriptionKey={`elearning.features.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16 px-4">
        <Reveal animation="fade-up">
          <div className="max-w-md mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <IconBadge icon={Bell} variant="orange" size="sm" />
              <EditableText
                editKey="elearning.waitlist.title"
                defaultValue="Être prévenu du lancement"
                as="h2"
                className="font-semibold text-slate-900 text-lg"
              />
            </div>
            <EditableText
              editKey="elearning.waitlist.subtitle"
              defaultValue="La plateforme sera accessible gratuitement aux inscrits au cursus annuel. Laissez votre email pour être prévenu du lancement."
              as="p"
              multiline
              className="text-slate-600 text-sm mb-8 leading-relaxed"
            />
            <form onSubmit={handleWaitlist} className="space-y-4 text-left">
              <div className="space-y-2">
                <Label htmlFor="waitlist-name" className="text-slate-600 text-sm">
                  Nom (optionnel)
                </Label>
                <Input
                  id="waitlist-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                  placeholder="Votre nom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist-email" className="text-slate-600 text-sm">
                  Email *
                </Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="votre@email.com"
                />
              </div>
              <Button type="submit" disabled={loading} className={`w-full ${BTN_PRIMARY}`}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi…
                  </>
                ) : (
                  <EditableText
                    editKey="elearning.waitlist.submit"
                    defaultValue="Me prévenir du lancement"
                    as="span"
                  />
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1.5 pt-1">
                <Lock className="h-3 w-3" aria-hidden="true" />
                <EditableText
                  editKey="elearning.waitlist.privacy_note"
                  defaultValue="Vos données restent confidentielles"
                  as="span"
                />
              </p>
            </form>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
};

export default Elearning;
